import { CustomSelect } from '@/components';
import AutocompleteInput, { Place } from '@/components/AutocompleteInput/AutocompleteInput';
import { Column, FormField } from '@/components/SettingsLayout';
import { Text, Divider, Input, SwitchInput } from '@/ui';
import { Country, State } from 'country-state-city';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.css';

export const industriesDropDown = [
  {
    label: 'payment_facility',
    value: 'payment_facility',
  },
  {
    label: 'ecommerce_platforms',
    value: 'ecommerce_platforms',
  },
  {
    label: 'franchisor',
    value: 'franchisor',
  },
  {
    label: 'hardware_distributors',
    value: 'hardware_distributors',
  },
  {
    label: 'web_design_agencies',
    value: 'web_design_agencies',
  },
  {
    label: 'saas_productions',
    value: 'saas_productions',
  },
  {
    label: 'solo_entrepreneur',
    value: 'solo_entrepreneur',
  },
  {
    label: 'other',
    value: 'other',
  },
];
export const OrganizationForm = () => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },

    setValue,
    watch,
    control,
  } = useFormContext();

  const chosenCountry = watch('country');
  const address = watch('address');
  const shippingAddress = watch('shipping.address');

  const handlePlaceSelect = (place: Place, mode: 'billing' | 'shipping' = 'billing') => {
    const formatted_address = place.formatted_address;
    if (mode === 'billing') {
      setValue('address', formatted_address);
    }

    if (mode === 'shipping') {
      setValue('shipping.address', formatted_address);
    }

    const cityComponent = place.address_components?.find(
      (component) =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_1') ||
        component.types.includes('administrative_area_level_2') ||
        component.types.includes('administrative_area_level_3'),
    );
    if (cityComponent) {
      setValue(mode === 'billing' ? 'city' : 'shipping.city', cityComponent.long_name);
      setValue('shipping.city', cityComponent.long_name);
    }
    const postalCodeComponent = place.address_components.find((component) =>
      component.types.includes('postal_code'),
    );
    if (postalCodeComponent) {
      setValue(
        mode === 'billing' ? 'postCode' : 'shipping.postCode',
        postalCodeComponent.long_name,
      );
    }
    const stateComponent = place.address_components.find((component) =>
      component.types.includes('administrative_area_level_1'),
    );
    if (stateComponent && chosenCountry?.value) {
      const newState = State.getStateByCodeAndCountry(
        stateComponent.short_name,
        chosenCountry.value,
      );
      if (newState) {
        setValue(mode === 'billing' ? 'state' : 'shipping.state', {
          value: newState.isoCode,
          label: newState.name,
        });
      }
    }
    const countryComponent = place.address_components.find((component) =>
      component.types.includes('country'),
    );
    if (countryComponent) {
      const countryValue = countryComponent.short_name;
      const countryLabel = countryComponent.long_name;
      setValue(mode === 'billing' ? 'country' : 'shipping.country', {
        value: countryValue,
        label: countryLabel,
      });
    }
  };

  // TODO: Add image upload but not in the token!!!

  // const onUploadImage = () => {
  //   inputRef.current?.click();
  // };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     clearErrors('profileImage');
  //     if (file.size > COMPANY_LOGO_MAX_SIZE) {
  //       setError('profileImage', {
  //         message: t('Settings.logo_size_error'),
  //       });
  //       return;
  //     } else {
  //       const fileUrl = URL.createObjectURL(file);
  //       setValue('profileImageFile', file);
  //       setValue('profileImage', fileUrl);
  //     }
  //   }
  // };

  return (
    <Column gapSize='24'>
      <Column gapSize='16'>
        <FormField className='grid'>
          <label htmlFor='org_name'>
            <Text size='s' weight='light-medium'>
              {t('Accounts.organization_name')}
            </Text>
          </label>
          <Input
            id='org_name'
            placeholder='name'
            {...register('org_name', {
              required: t('Form.required.org_name'),
            })}
          />
          {errors.org_name && (
            <Text size='xxs' weight='light-medium' color='red'>
              {t('Form.errors.name')}
            </Text>
          )}
        </FormField>
        <FormField className='grid'>
          <label htmlFor='industry'>
            <Text size='s' weight='light-medium'>
              {t('Accounts.industry')}
            </Text>
          </label>
          <Controller
            name={'industry'}
            control={control}
            rules={{ required: t('Form.required.industry') }}
            render={({ field }) => {
              const options = industriesDropDown.map((industry) => ({
                value: industry.value,
                label: t(`Accounts.${industry.label}`),
              }));
              return (
                <CustomSelect
                  options={options}
                  variant={'full'}
                  isSearchable={true}
                  field={field}
                  inputId='industry'
                />
              );
            }}
          />
        </FormField>
        <Text size='s' weight='semi-bold'>
          {t('Accounts.billing_address')}
        </Text>
        <FormField className='grid'>
          <label htmlFor='address'>
            <Text size='s' weight='light-medium'>
              {t('Shared.address')}
            </Text>
          </label>
          <Controller
            name='address'
            control={control}
            rules={{ required: t('Form.required.address') }}
            render={({ field }) => (
              <AutocompleteInput
                {...field}
                onChange={(place) => {
                  handlePlaceSelect(place, 'billing');
                  // Optionally, also call field.onChange if needed
                  field.onChange(place.formatted_address);
                }}
                placeholder={t('Form.placeholders.address')}
                value={address as string}
                defaultValue={address}
              />
            )}
          />
          {errors?.address && (
            <Text size='xxs' weight='light-medium' color='red'>
              {errors.address?.message as string}
            </Text>
          )}
        </FormField>
        <FormField className='grid'>
          <label htmlFor='city'>
            <Text size='s' weight='light-medium'>
              {t('Shared.city')}
            </Text>
          </label>
          <Input
            id='city'
            placeholder={t('Form.placeholders.city')}
            {...register('city', { required: t('Form.required.city') })}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='country'>
            <Text size='s' weight='light-medium'>
              {t('Shared.country')}
            </Text>
          </label>
          <Controller
            name={'country'}
            control={control}
            rules={{ required: t('Form.required.country') }}
            render={({ field }) => {
              const options = Country.getAllCountries().map((country) => ({
                value: country.isoCode,
                label: country.name,
              }));

              return (
                <>
                  <CustomSelect
                    options={options}
                    variant={'full'}
                    isSearchable={true}
                    field={field}
                    inputId='country'
                  />
                  {errors?.state && (
                    <Text size='xxs' weight='light-medium' color='red'>
                      {errors.state?.message as string}
                    </Text>
                  )}
                </>
              );
            }}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='state'>
            <Text size='s' weight='light-medium'>
              {t('Shared.state_province')}
            </Text>
          </label>
          <Controller
            name={'state'}
            control={control}
            rules={{ required: t('Form.required.state') }}
            render={({ field }) => {
              const country = watch('country')?.value;
              const options = State.getStatesOfCountry(country).map((state) => ({
                value: state.isoCode,
                label: state.name,
              }));

              return (
                <>
                  <CustomSelect
                    options={options}
                    variant={'full'}
                    isSearchable={true}
                    field={field}
                    inputId='state'
                  />
                  {errors?.state && (
                    <Text size='xxs' weight='light-medium' color='red'>
                      {errors.state?.message as string}
                    </Text>
                  )}
                </>
              );
            }}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='postCode'>
            <Text size='s' weight='light-medium'>
              {t('Shared.postal_code')}
            </Text>
          </label>
          <Input
            id='postCode'
            placeholder={t('Form.placeholders.postal_code')}
            {...register('postCode', { required: t('Form.required.postCode') })}
          />
        </FormField>
        <Text size='s' weight='semi-bold'>
          {t('Accounts.shipping_address')}
        </Text>
        <FormField className='grid'>
          <label htmlFor='shipping_address'>
            <Text size='s' weight='light-medium'>
              {t('Shared.address')}
            </Text>
          </label>
          <Controller
            name='shipping.address'
            control={control}
            rules={{ required: t('Form.required.address') }}
            render={({ field }) => (
              <AutocompleteInput
                {...field}
                onChange={(place) => {
                  handlePlaceSelect(place, 'shipping');
                  field.onChange(place.formatted_address);
                }}
                placeholder={t('Form.placeholders.address')}
                value={shippingAddress as string}
                defaultValue={shippingAddress}
              />
            )}
          />
          {errors?.shipping && 'address' in errors.shipping && (
            <Text size='xxs' weight='light-medium' color='red'>
              {(errors.shipping as any).address?.message as string}
            </Text>
          )}
        </FormField>
        <FormField className='grid'>
          <label htmlFor='shipping_city'>
            <Text size='s' weight='light-medium'>
              {t('Shared.city')}
            </Text>
          </label>
          <Input
            id='shipping_city'
            placeholder={t('Form.placeholders.city')}
            {...register('shipping.city', {
              required: t('Form.required.city'),
            })}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='shipping_country'>
            <Text size='s' weight='light-medium'>
              {t('Shared.country')}
            </Text>
          </label>
          <Controller
            name={'shipping.country'}
            control={control}
            rules={{ required: t('Form.required.country') }}
            render={({ field }) => {
              const options = Country.getAllCountries().map((country) => ({
                value: country.isoCode,
                label: country.name,
              }));
              return (
                <>
                  <CustomSelect
                    options={options}
                    variant={'full'}
                    isSearchable={true}
                    field={field}
                    inputId='shipping_country'
                  />
                  {errors?.state && (
                    <Text size='xxs' weight='light-medium' color='red'>
                      {errors.state?.message as string}
                    </Text>
                  )}
                </>
              );
            }}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='shipping_state'>
            <Text size='s' weight='light-medium'>
              {t('Shared.state_province')}
            </Text>
          </label>
          <Controller
            name={'shipping.state'}
            control={control}
            rules={{ required: t('Form.required.state') }}
            render={({ field }) => {
              const selectedCountry = watch('shipping.country')?.value;
              const options = State.getStatesOfCountry(selectedCountry).map((state) => ({
                value: state.isoCode,
                label: state.name,
              }));

              return (
                <>
                  <CustomSelect
                    options={options}
                    variant={'full'}
                    isSearchable={true}
                    field={field}
                    inputId='shipping_state'
                    menuPosition='fixed'
                  />
                  {errors?.state && (
                    <Text size='xxs' weight='light-medium' color='red'>
                      {errors.state?.message as string}
                    </Text>
                  )}
                </>
              );
            }}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='shipping_postCode'>
            <Text size='s' weight='light-medium'>
              {t('Shared.postal_code')}
            </Text>
          </label>
          <Input
            id='shipping_postCode'
            placeholder={t('Form.placeholders.postal_code')}
            {...register('shipping.postCode', {
              required: t('Form.required.postCode'),
            })}
          />
        </FormField>
      </Column>
      <Divider />
      <Column gapSize='16'>
        <Text size='s' weight='semi-bold'>
          {t('Accounts.organization_owner')}
        </Text>
        {/* <FormField className='grid'>
          <label htmlFor='image'>
            <Text size='s' weight='light-medium'>
              {t('Accounts.profile_image')}
            </Text>
          </label>
          <div className={styles.imgContainer}>
            <div className={styles.imgPlaceholder}>
              <img
                src={watch('profileImage') ?? '/placeholder.svg'}
                width={24}
              />
            </div>

            <Column>
              <div className={styles.imgInputContainer}>
                <input
                  type='file'
                  name='image'
                  id='image'
                  ref={inputRef}
                  onChange={handleFileChange}
                />
                <Button variant='link' onClick={onUploadImage} type='button'>
                  {t('Accounts.upload_new_image')}
                </Button>
              </div>
              <Text size='xxs' weight='light-medium' color='base-grey'>
                {t('Accounts.max_size')}
              </Text>
            </Column>
          </div>
          {errors.profileImage?.message && (
            <Text size='xxs' weight='light-medium' color='red'>
              {errors.profileImage?.message as string}
            </Text>
          )}
        </FormField> */}
        <FormField className='grid'>
          <label htmlFor='first_name'>
            <Text size='s' weight='light-medium'>
              {t('Form.first_name')}
            </Text>
          </label>
          <Input
            id='first_name'
            placeholder={t('Accounts.organization_owner_name')}
            {...register('first_name', {
              required: t('Form.required.name'),
            })}
          />
          {errors?.first_name && (
            <Text size='xxs' weight='light-medium' color='red'>
              {errors.first_name?.message as string}
            </Text>
          )}
        </FormField>
        <FormField className='grid'>
          <label htmlFor='last_name'>
            <Text size='s' weight='light-medium'>
              {t('Form.lastName')}
            </Text>
          </label>
          <Input
            id='last_name'
            placeholder={t('Form.placeholders.lastName')}
            {...register('last_name', {
              required: t('Form.required.lastName'),
            })}
          />
          {errors?.last_name && (
            <Text size='xxs' weight='light-medium' color='red'>
              {errors.last_name?.message as string}
            </Text>
          )}
        </FormField>
        <Text size='s' weight='semi-bold'>
          {t('Accounts.log_in_info')}
        </Text>
        <FormField className='grid'>
          <label htmlFor='email'>
            <Text size='s' weight='light-medium'>
              {t('Form.email')}
            </Text>
          </label>
          <Input
            id='email'
            placeholder={t('Form.placeholders.email')}
            {...register('email', {
              required: t('Form.required.email'),
            })}
          />
        </FormField>
        <FormField className='grid'>
          <label htmlFor='password'>
            <Text size='s' weight='light-medium'>
              {t('Form.password')}
            </Text>
          </label>
          <Input
            id='password'
            placeholder={t('Login.passPlaceholder')}
            type='password'
            {...register('password', {
              required: t('Login.errorPass'),
            })}
          />
        </FormField>
        <FormField className='grid'>
          <Text size='s' weight='light-medium'>
            {t('Accounts.permissions')}
          </Text>
          <Controller
            name={'builder_permission'}
            control={control}
            render={({ field }) => (
              <div className={styles.permissionSwitchContainer}>
                <SwitchInput field={field} checked={Boolean(field.value)} />
              </div>
            )}
          />
        </FormField>
      </Column>
    </Column>
  );
};
